import { v5 as uuidv5 } from "uuid";
import { coordKey, refLabel } from "./parse";
import type {
  ExistingState,
  LegacyHighlight,
  LegacyImportPlan,
  LegacyIssue,
  LegacyPreferences,
  LegacySourceId,
  PlannedVerseHighlight,
  ResolvedImport,
  SkippedEntry,
} from "./types";

/**
 * Migration plan with DETERMINISTIC identity and no fabricated streak.
 *
 * ## Idempotence
 * There is no "already imported" marker: it is lost on reinstall or when changing
 * devices. Each row receives an id derived from its COORDINATE with UUIDv5, so
 * migrating twice produces the same ids and the second pass writes nothing.
 *
 * ## The streak is NOT fabricated
 * Legacy **has no timestamps** (the `highlight` table stores no date; the old app's
 * "date order" was `ORDER BY rowid DESC`). Stamping every imported highlight with
 * `now()` would inject a false spike into gamification, because bso derives its
 * streak from `created_at`.
 *
 * Adopted policy: **everything imported hangs from a fixed `origin_id`**
 * (`LEGACY_IMPORT_ORIGIN`) and streak derivation EXCLUDES that origin
 * (`gamification/derive.ts`). Thus:
 *   - the data remains intact and visible in the product;
 *   - the displayed date is honest (the day it was migrated);
 *   - historical streak and XP are NOT invented.
 */

/**
 * UUIDv5 namespace owned by the legacy migration.
 * NEVER change it: changing it would turn every re-migration into a duplicate.
 */
export const LEGACY_IMPORT_NAMESPACE = "6f1b6b3a-9b0e-5f43-9b7a-2b1c4a5d6e70";

/**
 * Fixed `origin_id` for EVERYTHING migrated from the old app.
 *
 * It is the stable, synchronizable marker—with no new columns—that lets us
 * exclude imported data from streak derivation and group it under `/highlights`.
 */
export const LEGACY_IMPORT_ORIGIN = uuidv5("legacy-import-group", LEGACY_IMPORT_NAMESPACE);

/** Group-row color (neutral: each verse keeps its real color). */
export const LEGACY_IMPORT_GROUP_COLOR = "#9CA3AF";

/** Deterministic id of a migrated highlight, derived from its coordinate. */
function verseHighlightId(ref: LegacyHighlight): string {
  return uuidv5(`legacy-verse-highlight|${coordKey(ref)}`, LEGACY_IMPORT_NAMESPACE);
}

/** Builds the plan. Pure: same legacy data → same plan. */
export function buildImportPlan(
  sources: readonly LegacySourceId[],
  highlights: readonly LegacyHighlight[],
  issues: readonly LegacyIssue[] = [],
  preferences?: LegacyPreferences,
): LegacyImportPlan {
  const verseHighlights: PlannedVerseHighlight[] = highlights.map((highlight) => ({
    id: verseHighlightId(highlight),
    version: highlight.version,
    book: highlight.book,
    chapter: highlight.chapter,
    verse: highlight.verse,
    color: highlight.color,
  }));

  return {
    sources: [...sources],
    groupId: LEGACY_IMPORT_ORIGIN,
    verseHighlights,
    preferences,
    issues: [...issues],
  };
}

/**
 * Crosses the plan with what ALREADY exists and decides what gets written.
 *
 * Rules (never destructive — the migration runs neither DELETE nor UPDATE):
 * - id already present ⇒ `already-imported`, it is not rewritten;
 * - verse already highlighted by the user ⇒ **the user's** is kept and the legacy
 *   one is omitted. A verse has ONE color (the legacy PK was one too), so
 *   "keep both" does not exist: what the user chose always wins;
 * - the reading position moves only if the user does NOT already have one;
 * - the remaining preferences are REPORTED but not applied: overwriting the current
 *   theme or typography would be destructive, and the user already chose them here.
 */
export function resolveImport(plan: LegacyImportPlan, existing: ExistingState): ResolvedImport {
  const skipped: SkippedEntry[] = [];

  const verseHighlights = plan.verseHighlights.filter((row) => {
    if (existing.ids.has(row.id)) {
      skipped.push({ id: row.id, reason: "already-imported", ref: refLabel(row) });
      return false;
    }
    if (existing.highlightedCoords.has(coordKey(row))) {
      skipped.push({ id: row.id, reason: "verse-already-highlighted", ref: refLabel(row) });
      return false;
    }
    return true;
  });

  const position = plan.preferences;
  const canMoveReadingPosition =
    !existing.hasLastRead &&
    position?.currentBook !== undefined &&
    position.currentBook >= 1 &&
    position.currentBook <= 66;

  return {
    // The group is only needed if there is something to attach to it and it does not exist yet.
    createGroup: !existing.groupExists && verseHighlights.length > 0,
    groupId: plan.groupId,
    verseHighlights,
    appliedPreferences: canMoveReadingPosition
      ? {
          currentBook: position.currentBook,
          currentChapter: position.currentChapter ?? 1,
          currentVerse: position.currentVerse ?? 1,
        }
      : undefined,
    skipped,
  };
}
